import React from 'react';
import { CheckCircle, Award } from 'lucide-react';
import { useApp } from '../context/AppContext';

export const GradingPage = () => {
  const { t } = useApp();

  const grades = [
    {
      grade: 'A',
      title: t('grading.gradeA.title'),
      subtitle: t('grading.gradeA.subtitle'),
      desc: t('grading.gradeA.desc'),
      color: '#10B981',
      features: t('grading.gradeAItems'),
    },
    {
      grade: 'B',
      title: t('grading.gradeB.title'),
      subtitle: t('grading.gradeB.subtitle'),
      desc: t('grading.gradeB.desc'),
      color: '#F59E0B',
      features: t('grading.gradeBItems'),
    },
    {
      grade: 'C',
      title: t('grading.gradeC.title'),
      subtitle: t('grading.gradeC.subtitle'),
      desc: t('grading.gradeC.desc'),
      color: '#EF4444',
      features: t('grading.gradeCItems'),
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="grading-page">
      {/* Hero */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00D4FF]/10 text-[#00D4FF] text-sm font-medium mb-6">
            <Award className="w-4 h-4" />
            Quality Standards
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" data-testid="grading-title">
            {t('grading.title')}
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            {t('grading.subtitle')}
          </p>
        </div>
      </section>

      {/* Grades */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {grades.map((item, idx) => (
              <div
                key={item.grade}
                className="card-dark p-8 relative overflow-hidden group"
                data-testid={`grade-card-${item.grade.toLowerCase()}`}
              >
                {/* Grade Badge */}
                <div 
                  className="absolute top-0 right-0 w-24 h-24 flex items-center justify-center"
                  style={{ 
                    background: `linear-gradient(135deg, ${item.color}20 0%, transparent 60%)` 
                  }}
                >
                  <span 
                    className="text-4xl font-bold"
                    style={{ color: item.color }}
                  >
                    {item.grade}
                  </span>
                </div>

                {/* Content */}
                <div className="relative">
                  <h2 className="text-2xl font-bold mb-2">{item.title}</h2>
                  <div 
                    className="text-sm font-medium mb-4"
                    style={{ color: item.color }}
                  >
                    {item.subtitle}
                  </div>
                  <p className="text-white/60 mb-8">{item.desc}</p>

                  {/* Features */}
                  <ul className="space-y-3">
                    {item.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <CheckCircle 
                          className="w-5 h-5 mt-0.5 flex-shrink-0"
                          style={{ color: item.color }}
                        />
                        <span className="text-white/80">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="glass p-8">
            <h3 className="text-xl font-semibold mb-4 gradient-text">{t('grading.importantNote')}</h3>
            <p className="text-white/70 leading-relaxed">
              {t('grading.importantNoteText')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
